import { perspectiveFactorForDistance, type ReferenceDistances } from "./markerScaling";

/**
 * Sizing and styling constants for the pin placement highlight disc.
 *
 * The disc's screen radius is baseRadiusPx x the pin perspective factor
 * (see markerScaling.ts) — the exact distance curve pins are rendered with.
 * Pin and disc therefore grow, shrink, and clamp together while zooming.
 */
export const PLACEMENT_HIGHLIGHT = {
    /** Fill color of the disc. */
    color: "#00FF33",
    /** Opacity of the circle fill. */
    fillOpacity: 0.2,
    /** Opacity of the outer white outline. */
    outlineOpacity: 0.6,
    /**
     * White outline band as fractions of the fill radius. The space between
     * the fill edge (1) and outlineInner creates the visible border gap.
     */
    outlineInner: 1.107,
    outlineOuter: 1.197,
    /**
     * Disc fill radius in CSS pixels at pin perspective factor 1 (camera at
     * or inside the near reference distance). Sized relative to the default
     * 32px pin icon so the disc-to-pin proportion matches the prototype.
     */
    baseRadiusPx: 24,
} as const;

/**
 * Screen-space (CSS pixel) radius of the placement highlight disc for a given
 * camera-to-surface distance.
 *
 * Scales on the same perspective curve as the pins (markerScaling.ts), so the
 * disc stays visually locked to the pin and clamps at the same near/far
 * distances the pin does.
 *
 * Returns 0 when inputs cannot produce a meaningful radius.
 */
export function getPlacementDiscScreenRadius(dist: number, referenceDistances: ReferenceDistances): number {
    if (!Number.isFinite(dist) || dist <= 0) {
        return 0;
    }

    const radius = PLACEMENT_HIGHLIGHT.baseRadiusPx * perspectiveFactorForDistance(dist, referenceDistances);
    return Number.isFinite(radius) && radius > 0 ? radius : 0;
}
